import { useQuery, useMutation, useQueryClient } from "react-query";
import { healthFormsApi, QuestionListItem, QuestionListParams, QuestionnaireListItem, QuestionGroupListItem, SearchParams, UsageItem, PublishedQuestionnaireListItem, PublishedQuestionnaireDetail, PublishCheckConflict, PublishedQuestionnaireHistoryItem, PatientPreviewResponse, QuestionPreviewResponse, QuestionGroupPreviewResponse } from "./api";
import type { PreviewAttrs } from "./api";
import { Question } from "./types/common";
import { Questionnaire, QuestionGroup, ExpandedQuestionnaire, ExpandedQuestionGroup } from "./types/persisted";

export const queryKeys = {
  questionsBase: ["healthForms", "questions"] as const,
  questions: (params?: QuestionListParams) => ["healthForms", "questions", "list", params] as const,
  question: (id: number) => ["healthForms", "questions", id] as const,
  questionnaires: (params?: SearchParams) => ["healthForms", "questionnaires", "list", params] as const,
  questionnairesBase: ["healthForms", "questionnaires"] as const,
  questionnaire: (id: number) => ["healthForms", "questionnaires", id] as const,
  questionnaireByName: (name: string) => ["healthForms", "questionnaires", "by-name", name] as const,
  questionGroups: (params?: SearchParams) => ["healthForms", "questionGroups", "list", params] as const,
  questionGroupsBase: ["healthForms", "questionGroups"] as const,
  questionGroup: (id: number) => ["healthForms", "questionGroups", id] as const,
  questionUsage: (id: number) => ["healthForms", "questions", id, "usage"] as const,
  questionGroupUsage: (id: number) => ["healthForms", "questionGroups", id, "usage"] as const,
  publishedQuestionnaires: (params?: SearchParams) => ["healthForms", "publishedQuestionnaires", "list", params] as const,
  publishedQuestionnairesBase: ["healthForms", "publishedQuestionnaires"] as const,
  publishedQuestionnaire: (id: number) => ["healthForms", "publishedQuestionnaires", id] as const,
  questionnairePreview: (id: number, attrs: PreviewAttrs) => ["healthForms", "questionnaires", id, "preview", attrs] as const,
  questionPreview: (id: number, attrs: PreviewAttrs) => ["healthForms", "questions", id, "preview", attrs] as const,
  questionGroupPreview: (id: number, attrs: PreviewAttrs) => ["healthForms", "questionGroups", id, "preview", attrs] as const,
  publishedQuestionnairePreview: (id: number, attrs: PreviewAttrs) => ["healthForms", "publishedQuestionnaires", id, "preview", attrs] as const,
  publishedQuestionnaireHistory: (name: string) => ["healthForms", "publishedQuestionnaires", "history", name] as const,
  publishCheck: (id: number) => ["healthForms", "publishCheck", id] as const,
};

export const useQuestions = (params?: QuestionListParams) =>
  useQuery<QuestionListItem[]>(queryKeys.questions(params), () => healthFormsApi.questions.list(params));

export const useQuestion = (id: number | null) =>
  useQuery<Question>(queryKeys.question(id ?? 0), () => healthFormsApi.questions.get(id!), {
    enabled: !!id,
  });

export const useUpdateQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, question }: { id: number; question: Question }) =>
      healthFormsApi.questions.update(id, question),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(queryKeys.questionsBase);
        queryClient.invalidateQueries(queryKeys.question(id));
        queryClient.invalidateQueries(queryKeys.questionnairesBase);
      },
    }
  );
};

export const useDeleteQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => healthFormsApi.questions.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.questionsBase);
    },
  });
};

export const useQuestionnaires = (params?: SearchParams) =>
  useQuery<QuestionnaireListItem[]>(queryKeys.questionnaires(params), () => healthFormsApi.questionnaires.list(params));

export const useQuestionnaire = (id: number | null) =>
  useQuery<ExpandedQuestionnaire>(queryKeys.questionnaire(id ?? 0), () => healthFormsApi.questionnaires.get(id!), {
    enabled: !!id,
  });

export const useQuestionnaireByName = (name: string | null) =>
  useQuery<QuestionnaireListItem | null>(
    queryKeys.questionnaireByName(name ?? ""),
    () => healthFormsApi.questionnaires.getByName(name!),
    { enabled: !!name },
  );

export const useUpdateQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, questionnaire }: { id: number; questionnaire: Questionnaire }) => healthFormsApi.questionnaires.update(id, questionnaire),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(queryKeys.questionnairesBase);
        queryClient.invalidateQueries(queryKeys.questionnaire(id));
      },
    }
  );
};

export const useDeleteQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => healthFormsApi.questionnaires.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.questionnairesBase);
    },
  });
};

export const useQuestionGroups = (params?: SearchParams) =>
  useQuery<QuestionGroupListItem[]>(queryKeys.questionGroups(params), () => healthFormsApi.questionGroups.list(params));

export const useQuestionGroup = (id: number | null) =>
  useQuery<ExpandedQuestionGroup>(
    queryKeys.questionGroup(id ?? 0),
    () => healthFormsApi.questionGroups.get(id!),
    {
      enabled: !!id,
    }
  );

export const useUpdateQuestionGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, group }: { id: number; group: QuestionGroup }) =>
      healthFormsApi.questionGroups.update(id, group),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(queryKeys.questionGroupsBase);
        queryClient.invalidateQueries(queryKeys.questionGroup(id));
      },
    }
  );
};

export const useDeleteQuestionGroup = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => healthFormsApi.questionGroups.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.questionGroupsBase);
    },
  });
};

export const useCreateQuestion = () => {
  const queryClient = useQueryClient();
  return useMutation(() => healthFormsApi.questions.create(), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.questionsBase);
    },
  });
};

export const useCreateQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (input?: { name?: string; pages?: Questionnaire["pages"] }) => healthFormsApi.questionnaires.create(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.questionnairesBase);
      },
    }
  );
};

export const useCreateQuestionGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (input?: Omit<QuestionGroup, "_type" | "id" | "updated_at" | "edited_by">) => healthFormsApi.questionGroups.create(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.questionGroupsBase);
      },
    }
  );
};

export const useQuestionUsage = (id: number) =>
  useQuery<UsageItem[]>(queryKeys.questionUsage(id), () => healthFormsApi.questions.usage(id));

export const useQuestionGroupUsage = (id: number) =>
  useQuery<UsageItem[]>(queryKeys.questionGroupUsage(id), () => healthFormsApi.questionGroups.usage(id));

export const usePublishedQuestionnaires = (params?: SearchParams) =>
  useQuery<PublishedQuestionnaireListItem[]>(queryKeys.publishedQuestionnaires(params), () => healthFormsApi.publishedQuestionnaires.list(params));

export const usePublishedQuestionnaire = (id: number | null) =>
  useQuery<PublishedQuestionnaireDetail>(
    queryKeys.publishedQuestionnaire(id ?? 0),
    () => healthFormsApi.publishedQuestionnaires.get(id!),
    { enabled: !!id }
  );

export const useQuestionnairePreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<PatientPreviewResponse>(
    queryKeys.questionnairePreview(id ?? 0, attrs),
    () => healthFormsApi.questionnaires.preview(id!, attrs),
    { enabled: !!id }
  );

export const useQuestionPreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<QuestionPreviewResponse>(
    queryKeys.questionPreview(id ?? 0, attrs),
    () => healthFormsApi.questions.preview(id!, attrs),
    { enabled: !!id }
  );

export const useQuestionGroupPreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<QuestionGroupPreviewResponse>(
    queryKeys.questionGroupPreview(id ?? 0, attrs),
    () => healthFormsApi.questionGroups.preview(id!, attrs),
    { enabled: !!id }
  );

export const usePublishedQuestionnairePreview = (id: number | null, attrs: PreviewAttrs) =>
  useQuery<PatientPreviewResponse>(
    queryKeys.publishedQuestionnairePreview(id ?? 0, attrs),
    () => healthFormsApi.publishedQuestionnaires.preview(id!, attrs),
    { enabled: !!id }
  );

export const usePublishCheck = (id: number | null) =>
  useQuery<PublishCheckConflict | null>(
    queryKeys.publishCheck(id ?? 0),
    () => healthFormsApi.questionnaires.publishCheck(id!),
    { enabled: !!id }
  );

export const usePublishedQuestionnaireHistory = (name: string | null) =>
  useQuery<PublishedQuestionnaireHistoryItem[]>(
    queryKeys.publishedQuestionnaireHistory(name ?? ""),
    () => healthFormsApi.publishedQuestionnaires.history(name!),
    { enabled: !!name }
  );

export const usePublishQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number) => healthFormsApi.questionnaires.publish(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.publishedQuestionnairesBase);
      },
    }
  );
};

export const useInactivatePublishedQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: number) => healthFormsApi.publishedQuestionnaires.inactivate(id),
    {
      onSuccess: (_, id) => {
        queryClient.invalidateQueries(queryKeys.publishedQuestionnairesBase);
        queryClient.invalidateQueries(queryKeys.publishedQuestionnaire(id));
      },
    }
  );
};
